import { Chip, Divider } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useInvoiceLogs, type InvoiceLogRow } from '@/api/invoices';
import { formatDateTime } from '@/lib/format';
import { humanize } from '@/pages/customers/detail/status-tones';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** One update-history entry: type chip, description, then who + when. */
function LogEntry({ log }: { log: InvoiceLogRow }) {
  const type = log.log_type ?? (log.invoice_update_type as string | undefined);
  const description = log.log_description ?? (log.invoice_update_description as string | undefined);
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-1)',
        gridTemplateColumns: 'minmax(0, 1fr)',
        paddingBottom: 'var(--vn-space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        {type ? (
          <Chip appearance="subtle" tone="info" size="sm">
            {humanize(type)}
          </Chip>
        ) : null}
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {[log.employee_name, formatDateTime(log.created_at)].filter(Boolean).join(' · ')}
        </span>
      </div>
      {description ? (
        <span style={{ fontSize: 'var(--vn-font-size-sm)', overflowWrap: 'anywhere' }}>{description}</span>
      ) : null}
    </div>
  );
}

/**
 * Logs tab — the invoice's update history as a compact list (type + description +
 * who/when), mirroring the legacy `ListInvoiceUpdate`. Empty for many invoices.
 */
export function InvoiceLogsTab({ id }: { id: number }) {
  const { data, isLoading, isError, refetch } = useInvoiceLogs(id);
  const rows = data?.results ?? [];

  if (isError) return <TabError title="Failed to load activity" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={3} />;
  if (rows.length === 0)
    return <EmptyState title="No activity" description="No updates recorded for this invoice yet." />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {rows.map((log, i) => (
        <div key={log.invoice_update_id ?? log.log_id ?? i}>
          <LogEntry log={log} />
          {i < rows.length - 1 && <Divider />}
        </div>
      ))}
    </div>
  );
}
